import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory, useLocation } from 'react-router-dom'

import { fetchQAScoresByCallId } from '@/reducers/callSearch/callSearch.actions'
import { Menu } from '@/components/navigation/Menu'
import { countOpenRequests } from '@/views/VoiceAgents/changeRequestHelpers'
import { ActivityViewer } from '@/views/Calls/components/ActivityViewer'
import { fetchScorecardsByCallId } from '@/reducers/scorecards/scorecards.actions'
import { fetchQaAuditEventsByCallId } from '@/reducers/scorecards/scores.actions'
import { setData } from '@/reducers/scorecards/scorecards.redux'
import { CHANNEL_TYPE } from '@/utils/constants'

import { EventsViewer } from './components/EventsViewer'
import { Summary } from './components/Summary'
import { TechnicalDetails } from './components/TechnicalDetails'
import { CallQAScores } from './components/CallQAScores'
import { CallNotesTab } from './components/CallNotesTab'
import { AgentCommentsTab } from './components/AgentCommentsTab'
import {
  CALL_EXPLORER_TAB_ENUMS as enums,
  parseCallExplorerParams,
  updateCallExplorerParams,
} from './components/helpers'

import './CallExplorerTabs.scss'

export const CallExplorerTabs = ({
  highlightedEvents,
  callId,
  organizationId,
  callType = CHANNEL_TYPE.CALL,
  isAiCall = false,
}) => {
  const dispatch = useDispatch()
  const location = useLocation()
  const history = useHistory()
  const [openTab, setOpenTab] = useState(enums.SUMMARY)
  const { dynsight_session_id, tab: tabParam } = parseCallExplorerParams(location)
  const {
    callExplorer: { metadata = {}, analysis, copilotAnchors, agentActivityEvents },
  } = useSelector((state) => state.callSearch)
  const transcriptCommentCount = useSelector((state) =>
    countOpenRequests(state.voiceAgents.transcriptChangeRequests.items)
  )

  const dynamicInsightAnchors = localStorage.getItem('dynamicInsightsAnchors')
    ? JSON.parse(localStorage.getItem('dynamicInsightsAnchors'))
    : {}

  const dynsightAnchors = dynamicInsightAnchors?.[dynsight_session_id]?.anchors?.[callId] || []

  const callTabs = [
    { id: enums.SUMMARY, label: 'Summary' },
    { id: enums.DETAILS, label: 'Details' },
    // hide events tab for postcall only calls
    ...(metadata?.postcall_only ? [] : [{ id: enums.EVENTS, label: 'Events' }]),
    { id: enums.QA_SCORES, label: 'QA Scores' },
    { id: enums.COMMENTS, label: 'Comments' },
    // Transcript comments exist for voice-agent calls only.
    ...(isAiCall ? [{ id: enums.AGENT_COMMENTS, label: 'Transcript Comments' }] : []),
    { id: enums.ACTIVITY, label: 'Activity' },
  ]

  const tabs =
    callType !== 'call'
      ? [
          { id: enums.SUMMARY, label: 'Summary' },
          { id: enums.QA_SCORES, label: 'QA Scores' },
          { id: enums.ACTIVITY, label: 'Activity' },
        ]
      : callTabs

  const tabComponentMap = {
    [enums.EVENTS]: (
      <EventsViewer
        analysis={analysis}
        highlightedEvents={highlightedEvents}
        copilotAnchors={copilotAnchors}
        dynamicInsightsAnchors={dynsightAnchors}
        agentActivityEvents={agentActivityEvents}
        metadata={metadata}
      />
    ),
    [enums.SUMMARY]: <Summary callId={callId} callType={callType} />,
    [enums.DETAILS]: <TechnicalDetails metadata={metadata} callId={callId} />,
    [enums.QA_SCORES]: (
      <CallQAScores
        metadata={metadata}
        callId={callId}
        organizationId={organizationId}
        setOpenTab={setOpenTab}
        callType={callType}
      />
    ),
    [enums.ACTIVITY]: <ActivityViewer callType={callType} />,
    [enums.COMMENTS]: (
      <CallNotesTab callId={callId} organizationId={organizationId} setOpenTab={setOpenTab} />
    ),
    [enums.AGENT_COMMENTS]: <AgentCommentsTab callId={Number(callId)} />,
  }

  const fetchScoreData = async () => {
    const resetEvents = dispatch(setData('qaAuditEvents', []))

    const fetchScores = dispatch(fetchQAScoresByCallId(callId, organizationId, callType))
    const fetchScorecards = dispatch(fetchScorecardsByCallId(callId, organizationId, callType))
    const fetchAuditEvents = dispatch(fetchQaAuditEventsByCallId(callId, organizationId))

    await Promise.all([resetEvents, fetchScores, fetchScorecards, fetchAuditEvents])

    const { tab, keywords, events } = parseCallExplorerParams(location)

    if (tab) {
      setOpenTab(tab)
    }
    if (!tab && (keywords || events) && callType === 'call') {
      setOpenTab(enums.EVENTS)
    }
  }

  useEffect(() => {
    if (organizationId && callId) {
      fetchScoreData()
    }
  }, [organizationId, callId])

  // Follow the `tab` URL param so external navigation (e.g. clicking a comment
  // mark in the transcript) can open the matching tab. Ignore unknown values so
  // a stray `?tab=` can't select a tab that isn't in the menu (blank panel).
  useEffect(() => {
    if (tabParam && tabs.some((tab) => tab.id === tabParam)) {
      setOpenTab(tabParam)
    }
  }, [tabParam])

  return (
    <div className="flex-column flex-item call-explorer-panel">
      <header className="call-window-scrollable__headers call-explorer-tabs horizontal-scroll">
        <Menu
          menuStyle="pill"
          items={tabs.map((tab) => ({
            accessor: tab.id,
            label: tab.label,
            active: openTab === tab.id,
            onClick: () => {
              updateCallExplorerParams(location, history, { tab: tab.id })
              setOpenTab(tab.id)
            },
            dataTestId: tab.id,
            badge:
              tab.id === enums.AGENT_COMMENTS && transcriptCommentCount > 0
                ? transcriptCommentCount
                : undefined,
            badgeProps: { 'data-testid': 'transcript-comments-count' },
          }))}
        />
      </header>
      <div
        className="call-window-scrollable__content call-explorer-details-container flex-item"
        data-testid="call-explorer-details"
      >
        {tabComponentMap[openTab]}
      </div>
    </div>
  )
}
